import { keycloakClient } from "@/auth/keycloak";
import { envConfig } from "@/config/env.config";
import { AuthenticationError, AuthorizationError, OperationError } from "@/shared/errors/auth-errors";

const REFRESH_BUFFER_SECONDS = 30;

const ensureFreshToken = async (): Promise<string | undefined> => {
    const instance = keycloakClient.instance;
    if (instance.token === undefined) return undefined;
    try {
        await instance.updateToken(REFRESH_BUFFER_SECONDS);
    } catch (error) {
        throw new AuthenticationError("Failed to refresh access token", error);
    }
    return instance.token;
};

const buildHeaders = async (extra?: HeadersInit): Promise<Headers> => {
    const headers = new Headers(extra);
    const token = await ensureFreshTokenOrLogin();
    if (token !== undefined) {
        headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
};

const publicErrorCodes: Record<number, readonly string[]> = {
    400: ["ValidationError"],
    403: ["AllowanceError"],
    404: ["ExistenceError"],
    409: ["DuplicityError", "StateError"],
    422: ["FundsError"],
};

const publicErrorMessage = async (response: Response): Promise<string | null> => {
    const codes = publicErrorCodes[response.status];
    if (codes === undefined) return null;
    try {
        const body: unknown = await response.json();
        if (body === null || typeof body !== "object" || Array.isArray(body)
            || !("code" in body) || typeof body.code !== "string" || !codes.includes(body.code)
            || !("message" in body) || typeof body.message !== "string") return null;
        const message = body.message.trim();
        if (message === "" || message.length > 1000 || /[<>]/.test(message)
            || Array.from(message).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return null;
        return message;
    } catch {
        return null;
    }
};

const handleStatus = async (response: Response): Promise<void> => {
    if (response.status === 401) {
        keycloakClient.instance.login();
        throw new AuthenticationError("Unauthorized");
    }
    if (response.status === 403) {
        throw new AuthorizationError(await publicErrorMessage(response) ?? "Forbidden");
    }
    if (!response.ok) {
        throw new OperationError(await publicErrorMessage(response) ?? `HTTP ${response.status}`);
    }
};

const ensureFreshTokenOrLogin = async (): Promise<string | undefined> => {
    try {
        return await ensureFreshToken();
    } catch {
        keycloakClient.instance.login();
        throw new AuthenticationError("Unauthorized");
    }
};

const request = async (path: string, init?: RequestInit): Promise<Response> => {
    const headers = await buildHeaders(init?.headers);
    const response = await fetch(`${envConfig.apiBaseUrl}${path}`, { ...init, headers });
    await handleStatus(response);
    return response;
};

const fetchWithAuth = async (path: string, init?: RequestInit): Promise<Response> => {
    const headers = await buildHeaders(init?.headers);
    const response = await fetch(`${envConfig.apiBaseUrl}${path}`, { ...init, headers });
    if (response.status === 401) {
        keycloakClient.instance.login();
        throw new AuthenticationError("Unauthorized");
    }
    return response;
};

const getJson = async <T>(path: string, headers?: HeadersInit, signal?: AbortSignal): Promise<T> => {
    const response = await request(path, { method: "GET", headers, signal });
    return (await response.json()) as T;
};

const postJson = async <T>(path: string, body: unknown, headers?: HeadersInit, signal?: AbortSignal): Promise<T> => {
    const response = await request(path, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...Object.fromEntries(new Headers(headers).entries()) },
        body: JSON.stringify(body),
        signal,
    });
    return (await response.json()) as T;
};

const putJson = async <T>(path: string, body: unknown, headers?: HeadersInit): Promise<T> => {
    const response = await request(path, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...Object.fromEntries(new Headers(headers).entries()) },
        body: JSON.stringify(body),
    });
    return (await response.json()) as T;
};

const postBlob = async (path: string, body: unknown, headers?: HeadersInit): Promise<{ blob: Blob; contentDisposition: string | null }> => {
    const response = await request(path, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...Object.fromEntries(new Headers(headers).entries()) },
        body: JSON.stringify(body),
    });
    return { blob: await response.blob(), contentDisposition: response.headers.get("Content-Disposition") };
};

const deleteJson = async <T>(path: string, headers?: HeadersInit): Promise<T> => {
    const response = await request(path, { method: "DELETE", headers });
    return (await response.json()) as T;
};

const getBlob = async (path: string, signal?: AbortSignal): Promise<Blob> => {
    const response = await request(path, { method: "GET", signal });
    return response.blob();
};

const getText = async (path: string): Promise<string> => {
    const response = await request(path, { method: "GET" });
    return response.text();
};

export const httpClient = {
    request,
    fetchWithAuth,
    getJson,
    postJson,
    putJson,
    postBlob,
    deleteJson,
    getBlob,
    getText,
};
